import React, { useState, useEffect } from 'react';

import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { useSearchHeader } from '@src/contexts/SearchHeaderContext';

// Types
import { SearchSuggestion } from '@src/types/search';

interface SearchSuggestionsProps {
  title?: string;
  className?: string;
  settings: any;
  onSuggestionClick?: (suggestion: string) => void;
}

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  title = 'Search suggestions',
  className = '',
  settings,
  onSuggestionClick
}) => {
  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();
  const { suggestedQueries } = useSearchHeader();

  // Convert suggestedQueries from context to the format we need
  const suggestions = suggestedQueries?.map(query => ({
    text: query.text || query,
    styledText: query.styledText || query.text || query
  })) || null;

  const renderLimit = settings.max_results || 7;
  // Generate spacing classes from settings
  const spacingClasses = [
    settings.margin_top ? `mt-[${settings.margin_top}px]` : '',
    settings.margin_bottom ? `mb-[${settings.margin_bottom}px]` : '',
    settings.margin_left ? `ml-[${settings.margin_left}px]` : '',
    settings.margin_right ? `mr-[${settings.margin_right}px]` : '',
    settings.padding_top ? `pt-[${settings.padding_top}px]` : '',
    settings.padding_bottom ? `pb-[${settings.padding_bottom}px]` : '',
    settings.padding_left ? `pl-[${settings.padding_left}px]` : '',
    settings.padding_right ? `pr-[${settings.padding_right}px]` : ''
  ]
    .filter(Boolean)
    .join(' ');

  const handleClickSuggestion = (suggestion: string) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    } else {
      // Default behavior: redirect to search results page with the suggestion as query
      const searchUrl = `/search?q=${encodeURIComponent(suggestion)}`;
      window.location.href = searchUrl;
    }
  };

  // Suggestions now come from SearchHeaderContext, no need for local event handlers

  // No useEffect needed since suggestions come directly from context

  // Only show suggestions if we have predictive search query results
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className={`flex flex-col gap-2 pb-8 w-full ${spacingClasses} ${className}`}>
      <p className={`${isMobile || isTablet ? 'b1--bold' : 'b1--bold'}`}>{title}</p>

      <div className='flex flex-col gap-y-4 items-start'>
        {suggestions.slice(0, renderLimit).map((suggestion, index) => (
          <button
            key={index}
            onClick={() => handleClickSuggestion(suggestion.text)}
            className={`no-underline hover:underline ${isMobile || isTablet ? 'b3' : 'b3'}`}
            title={`View more search results for ${suggestion.text}`}
          >
            <span 
              dangerouslySetInnerHTML={{ __html: suggestion.styledText }}
              style={{
                '--mark-bg': 'transparent',
                '--mark-color': 'inherit',
                '--mark-font-weight': 'bold'
              } as React.CSSProperties}
              className="[&_mark]:bg-transparent [&_mark]:font-bold [&_mark]:text-inherit"
            ></span>
          </button>
        ))}
      </div>
    </div>
  );
};
